import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Table, CircularProgress,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs, Select,
    Tab, InputLabel,
    Button,
    TextField,
    MenuItem,
    FormControl,
    Grid, Box,
    useMediaQuery,
    useTheme,
    Switch,
    FormControlLabel,
    Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../utils/api';
import OrderRow from './OrderRow';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [userRole, setUserRole] = useState('');
    const [userLocation, setUserLocation] = useState('');

    // Separate search fields
    const [partySearch, setPartySearch] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [productLocation, setProductLocation] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showOnlyOrderedProducts, setShowOnlyOrderedProducts] = useState(true);
    const [locations] = useState(['Mill', 'Godown', 'Nora']);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 1,
        hasNext: false,
        hasPrev: false,
        totalRecords: 0,
        limit: 20
    });

    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Fetch orders with pagination and filters
    const fetchOrders = useCallback(async (page = 1, filters = {}) => {
        setIsLoading(true);
        try {
            // Determine status based on tab
            const status = filters.tabValue === 0 ? 'pending' : 'completed';

            // Build query parameters
            const params = {
                page,
                limit: filters.entriesPerPage || 20,
                status
            };

            // Add filters only if they have values
            if (filters.partySearch && filters.partySearch.trim()) {
                params.partySearch = filters.partySearch.trim();
            }
            if (filters.productSearch && filters.productSearch.trim()) {
                params.productSearch = filters.productSearch.trim();
            }
            if (filters.productLocation) {
                params.location = filters.productLocation;
            }
            if (filters.startDate) {
                params.startDate = filters.startDate;
            }
            if (filters.endDate) {
                params.endDate = filters.endDate;
            }

            const res = await api.get('/orders/paginated', { params });
            setOrders(res.data.orders);
            setPagination(res.data.pagination);
            setCurrentPage(page);

            // Set user details once
            if (!userRole) {
                setUserRole(localStorage.getItem('userRole'));
                if (localStorage.getItem('userRole') === 'employee') {
                    setUserLocation(localStorage.getItem('userLocation'));
                }
            }
        } catch (error) {
            console.error(`Error in fetching the orders:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [userRole]);

    // Initial load
    useEffect(() => {
        const role = localStorage.getItem('userRole');
        setUserRole(role);
        if (role === 'employee') {
            const location = localStorage.getItem('userLocation');
            setUserLocation(location);
            if (location !== 'Universal') {
                setProductLocation(location);
            }
        }
    }, []);

    // Helper to get current filters
    const getCurrentFilters = useCallback(() => ({
        tabValue,
        entriesPerPage,
        partySearch,
        productSearch,
        productLocation,
        startDate,
        endDate
    }), [tabValue, entriesPerPage, partySearch, productSearch, productLocation, startDate, endDate]);

    // Fetch when tab changes or entriesPerPage changes
    useEffect(() => {
        if (userRole) {
            fetchOrders(1, getCurrentFilters()); // Reset to page 1 when tab or entries change
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, entriesPerPage, userRole]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleEntriesPerPageChange = (event) => {
        setEntriesPerPage(event.target.value);
    };

    const handlePageChange = (event, value) => {
        fetchOrders(value, getCurrentFilters());
    };

    // Search button handler
    const handleSearch = () => {
        fetchOrders(1, getCurrentFilters()); // Reset to page 1 when searching
    };

    // Handle Enter key in search fields
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };


    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 0.5
                    }}
                >
                    Orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track and manage all your orders
                </Typography>
            </Box>

            {/* Filters Section */}
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Search by Party Name"
                            value={partySearch}
                            onChange={(e) => setPartySearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ style: { fontSize: '1rem' } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Search by Product Name"
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ style: { fontSize: '1rem' } }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                                style: { fontSize: '1rem' },
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                                style: { fontSize: '1rem' },
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    {(userRole === 'owner' || userLocation === 'Universal') && (
                        <Grid item xs={6} sm={4} md={2}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Product Location</InputLabel>
                                <Select
                                    value={productLocation}
                                    onChange={(e) => setProductLocation(e.target.value)}
                                    label="Product Location"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {locations.map((location) => (
                                        <MenuItem key={location} value={location}>{location}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    <Grid item xs={6} sm={4} md={userRole === 'owner' || userLocation === 'Universal' ? 2 : 4}>
                        <FormControl fullWidth variant="outlined">
                            <TextField
                                select
                                label="Entries per page"
                                value={entriesPerPage}
                                onChange={handleEntriesPerPageChange}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </TextField>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={userRole === 'owner' || userLocation === 'Universal' ? 3 : 4}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            sx={{ height: '40px' }}
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} md={userRole === 'owner' || userLocation === 'Universal' ? 3 : 4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyOrderedProducts}
                                    onChange={(e) => setShowOnlyOrderedProducts(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Show only pending products"
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} centered>
                    <Tab label={`Pending Orders ${tabValue === 0 && pagination.totalRecords > 0 ? `(${pagination.totalRecords})` : ''}`} />
                    <Tab label={`Completed Orders ${tabValue === 1 && pagination.totalRecords > 0 ? `(${pagination.totalRecords})` : ''}`} />
                </Tabs>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    <TableCell width="50px"></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Party Name</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Products</Typography></TableCell>
                                    {!isSmScreen && <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Status</Typography></TableCell>}
                                    {!isMdScreen && <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Created By</Typography></TableCell>}
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Created At</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No orders found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orders.map((order) => (
                                        <OrderRow
                                            key={order._id}
                                            order={order}
                                            userRole={userRole}
                                            userLocation={userLocation}
                                            onOrderUpdate={() => fetchOrders(currentPage, getCurrentFilters())}
                                            showOnlyOrdered={showOnlyOrderedProducts}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {pagination.total > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={pagination.total}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}

                    {/* Summary Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {orders.length > 0 ? ((currentPage - 1) * entriesPerPage + 1) : 0} to {Math.min(currentPage * entriesPerPage, pagination.totalRecords)} of {pagination.totalRecords} orders
                        </Typography>
                    </Box>
                </>
            )}
        </Container>
    );
};

export default Orders;
